/**
 * /telefoon-instellingen — admin-beheer van de telefonie-routering (multi-brand).
 *
 * Eén pagina voor alle merk-nummers: merk-switcher (chips) bovenaan, per
 * nummer eigen rooster/acties/begroetingen; de ring group is globaal (zelfde
 * team neemt alles op). "Nummer toevoegen" maakt een nieuwe settings-rij als
 * kopie van de Kleda-rij; "Toepassen op alle merken" trekt alleen het rooster
 * gelijk. Secties: live-preview (wat gebeurt er nú), openingstijden +
 * uitzonderingen, acties per situatie, begroetingen (TTS, per merk), ring
 * group. Alles settings-gedreven via /api/telephony/settings; wijzigingen
 * zijn binnen 60 seconden effectief (cache-TTL van de motor).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui';
import { supabase } from '../lib/supabase';
import {
  OpeningstijdenEditor,
  type WeekSchedule,
  type OverrideRow,
} from '../components/phone/settings/OpeningstijdenEditor';
import { GreetingsManager, type GeneratorPrefill } from '../components/phone/settings/GreetingsManager';
import { RingGroupManager } from '../components/phone/settings/RingGroupManager';
import { VoicePicker, type VoicesData } from '../components/phone/settings/VoicePicker';
import { AgentPanel } from '../components/phone/settings/AgentPanel';

interface RoutingAction {
  // ai_agent hoort hier expliciet bij: de select bood het al aan (ACTION_LABELS)
  // maar het type niet, dus elke wijziging werd door TypeScript naar een
  // bestaande variant gecast en de agent-stand was niet type-veilig te zetten.
  type: 'ring' | 'forward' | 'voicemail' | 'announce' | 'ai_agent';
  target?: string | null;
  greeting_id?: string | null;
}

interface GreetingOption {
  id: string;
  name: string;
  kind: string;
  active: boolean;
}

interface SettingsRow {
  id: string;
  phone_number: string;
  brand: string;
  timezone: string;
  schedule: WeekSchedule;
  in_hours_action: RoutingAction;
  overflow_action: RoutingAction;
  closed_action: RoutingAction;
  ring_timeout_seconds: number;
  record_calls: boolean;
  announce_greeting_id: string | null;
  updated_at: string;
  updated_by: string | null;
  overrides: Array<OverrideRow & { id?: string }>;
  availableGreetings: GreetingOption[];
}

interface PreviewDecision {
  stage: string;
  isOpen: boolean;
  targets: string[];
  reason: string;
  greetingUrl: string | null;
  greetingName: string | null;
  announceUrl: string | null;
  announceName: string | null;
}

interface PreviewResponse {
  at: string;
  flagActive: boolean;
  ringGroup: Array<{ sipUsername: string; userEmail: string | null }>;
  decision: PreviewDecision;
  afterRingTimeout: PreviewDecision | null;
  afterForwardFail: PreviewDecision | null;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

function describeDecision(d: PreviewDecision, ringGroup: PreviewResponse['ringGroup']): string {
  if (d.stage === 'ring') {
    const who = ringGroup.map((t) => t.userEmail || t.sipUsername).join(', ');
    return `Rinkelen bij ${d.targets.length} medewerker(s)${who ? `: ${who}` : ''}`;
  }
  if (d.stage === 'forward') return `Doorschakelen naar ${d.targets[0] ?? '?'}`;
  return 'Voicemail (begroeting + inspreken)';
}

function hhmmToMin(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  return m ? Number.parseInt(m[1] ?? '0', 10) * 60 + Number.parseInt(m[2] ?? '0', 10) : null;
}

/**
 * Eerstvolgende open/dicht-wissel vandaag (in de settings-timezone), of null.
 * Levert het tijdstip als label + een ISO-moment vlak ná de wissel, zodat de
 * preview-API kan vertellen wat er dán gebeurt (geen client-side duplicatie
 * van de begroetings-resolutie).
 */
function nextTransitionToday(
  schedule: WeekSchedule,
  overrides: OverrideRow[],
  timezone: string,
): { label: string; opens: boolean; atIso: string } | null {
  const now = new Date();
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone || 'Europe/Amsterdam',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', weekday: 'short', hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(now).map((p) => [p.type, p.value])) as Partial<
    Record<Intl.DateTimeFormatPartTypes, string>
  >;
  const weekdayMap: Record<string, string> = {
    Sun: 'sun', Mon: 'mon', Tue: 'tue', Wed: 'wed', Thu: 'thu', Fri: 'fri', Sat: 'sat',
  };
  const dateKey = `${parts.year ?? ''}-${parts.month ?? ''}-${parts.day ?? ''}`;
  const hour = parts.hour === '24' ? 0 : Number.parseInt(parts.hour ?? '0', 10);
  const nowMin = hour * 60 + Number.parseInt(parts.minute ?? '0', 10);

  const override = overrides.find((o) => o.date === dateKey);
  const weekdayKey = weekdayMap[(parts.weekday ?? '').replace('.', '')] ?? '';
  const blocks = override ? (override.closed ? [] : override.blocks ?? []) : schedule[weekdayKey] ?? [];

  const bounds: Array<{ min: number; opens: boolean }> = [];
  for (const b of blocks) {
    const from = hhmmToMin(b.from);
    const to = hhmmToMin(b.to);
    if (from !== null) bounds.push({ min: from, opens: true });
    if (to !== null) bounds.push({ min: to, opens: false });
  }
  bounds.sort((a, b) => a.min - b.min);
  const next = bounds.find((b) => b.min > nowMin);
  if (!next) return null;

  const label = `${String(Math.floor(next.min / 60)).padStart(2, '0')}:${String(next.min % 60).padStart(2, '0')}`;
  // Moment vlak ná de wissel: nu + verschil in minuten + 1 (binnen één dag DST-veilig genoeg).
  const atIso = new Date(now.getTime() + (next.min - nowMin + 1) * 60_000).toISOString();
  return { label, opens: next.opens, atIso };
}

/** Begroetingsregel bij een voicemail-stap: naam + beluisterknop, of de fallback-melding. */
function GreetingLine({ d, onPlay }: { d: PreviewDecision; onPlay: (url: string) => void }) {
  if (d.stage !== 'voicemail') return null;
  if (!d.greetingUrl) {
    return <span className="ml-1 text-amber-700">zonder begroeting (neutrale computerstem)</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5 ml-1">
      <span className="text-slate-400">met begroeting</span>
      <span className="text-slate-600 font-medium">"{d.greetingName ?? 'onbekend'}"</span>
      <button
        type="button"
        onClick={() => onPlay(d.greetingUrl as string)}
        className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-slate-200 text-[11px] font-medium"
      >
        Beluister
      </button>
    </span>
  );
}

const ACTION_LABELS: Record<string, string> = {
  ring: 'Rinkelen bij het team',
  forward: 'Doorschakelen',
  voicemail: 'Voicemail',
  // Zonder deze optie toont de select een ai_agent-config als 'ring' en
  // schrijft een save hem stilletjes terug naar ring — de flip zou dan door
  // een onschuldige UI-wijziging ongedaan gemaakt worden.
  ai_agent: 'AI-assistent (voice-agent)',
};

/** Leesbare merknamen voor de switcher-chips en dialogen. */
const BRAND_LABELS: Record<string, string> = {
  KLEDA: 'Kleda',
  CINCO: 'Cinco',
  SCHOONHUISSHOP: 'Schoonhuisshop',
  SOFASAVER: 'SofaSaver',
  VLOERKLEEDREINIGEN: 'Vloerkleedreinigen.com',
  BANKREINIGEN: 'Bankreinigen.nl',
};

const ALL_BRANDS = Object.keys(BRAND_LABELS);

function ActionEditor({
  label,
  hint,
  action,
  allowRing,
  greetings,
  onChange,
}: {
  label: string;
  hint: string;
  action: RoutingAction;
  allowRing: boolean;
  greetings: GreetingOption[];
  onChange: (action: RoutingAction) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 py-2.5">
      <div className="w-48">
        <div className="text-sm font-medium text-slate-800">{label}</div>
        <div className="text-xs text-slate-400">{hint}</div>
      </div>
      <select
        value={action.type}
        onChange={(e) => onChange({ ...action, type: e.target.value as RoutingAction['type'] })}
        className="border border-slate-200 rounded px-2 py-1.5 text-sm"
      >
        {Object.entries(ACTION_LABELS)
          .filter(([type]) => allowRing || type !== 'ring')
          .map(([type, l]) => (
            <option key={type} value={type}>{l}</option>
          ))}
      </select>
      {action.type === 'forward' && (
        <input
          type="tel"
          placeholder="+31850871663"
          value={action.target ?? ''}
          onChange={(e) => onChange({ ...action, target: e.target.value })}
          className="border border-slate-200 rounded px-2 py-1.5 text-sm font-mono w-44"
        />
      )}
      {action.type === 'voicemail' && (
        <select
          value={action.greeting_id ?? ''}
          onChange={(e) => onChange({ ...action, greeting_id: e.target.value || null })}
          className="border border-slate-200 rounded px-2 py-1.5 text-sm"
        >
          <option value="">Actieve begroeting (automatisch)</option>
          {greetings.map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
      )}
    </div>
  );
}

export function TelefoonInstellingenPage() {
  /** Alle settings-rijen (één per merk-nummer) voor de switcher-chips. */
  const [rows, setRows] = useState<SettingsRow[]>([]);
  /** De geselecteerde rij als bewerkbare werkkopie. */
  const [settings, setSettings] = useState<SettingsRow | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'dirty' | 'saving' | 'saved'>('idle');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addBrand, setAddBrand] = useState('');
  const [addNumber, setAddNumber] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  /** Welk nummer geselecteerd is — ref zodat fetchSettings geen stale closure heeft. */
  const selectedNumberRef = useRef<string | null>(null);
  /** Tijdmachine: datetime-local-waarde, leeg = nu. */
  const [previewAt, setPreviewAt] = useState('');
  const [nextTransition, setNextTransition] = useState<
    { label: string; opens: boolean; decision: PreviewDecision | null } | null
  >(null);
  const [voicesData, setVoicesData] = useState<VoicesData | null>(null);
  const [voicesLoading, setVoicesLoading] = useState(true);
  const [voicesError, setVoicesError] = useState<string | null>(null);
  const [generatorPrefill, setGeneratorPrefill] = useState<GeneratorPrefill | null>(null);
  /** Override-rij die wacht op een nieuw geschreven begroeting (auto-koppelen). */
  const pendingOverrideIndexRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const playAudio = useCallback((url: string) => {
    audioRef.current?.pause();
    audioRef.current = new Audio(url);
    void audioRef.current.play();
  }, []);

  const fetchSettings = useCallback(async (selectNumber?: string) => {
    try {
      const r = await fetch('/api/telephony/settings', { headers: await authHeaders() });
      if (!r.ok) throw new Error(`settings failed: ${r.status}`);
      const json = await r.json();
      const list = (json.settings ?? []) as SettingsRow[];
      if (list.length === 0) throw new Error('Geen telefonie-instellingen gevonden (migratie 036 toegepast?)');
      setRows(list);
      const target = selectNumber ?? selectedNumberRef.current;
      const row = list.find((s) => s.phone_number === target) ?? (list[0] as SettingsRow);
      selectedNumberRef.current = row.phone_number;
      setSettings(row);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Wisselen van merk-nummer; onopgeslagen wijzigingen niet stil weggooien.
  const switchToNumber = useCallback(
    (phoneNumber: string) => {
      if (phoneNumber === selectedNumberRef.current) return;
      if (saveState === 'dirty' || saveState === 'saving') {
        const ok = window.confirm(
          'Je hebt onopgeslagen wijzigingen. Wisselen van merk gooit ze weg. Doorgaan?',
        );
        if (!ok) return;
      }
      const row = rows.find((s) => s.phone_number === phoneNumber);
      if (!row) return;
      selectedNumberRef.current = phoneNumber;
      setSettings(row);
      setSaveState('idle');
      setError(null);
    },
    [rows, saveState],
  );

  const fetchPreview = useCallback(async (atValue?: string) => {
    try {
      const qs = new URLSearchParams({ fresh: '1' });
      if (selectedNumberRef.current) qs.set('number', selectedNumberRef.current);
      if (atValue) {
        const parsed = new Date(atValue);
        if (!Number.isNaN(parsed.getTime())) qs.set('at', parsed.toISOString());
      }
      const r = await fetch(`/api/telephony/routing-preview?${qs.toString()}`, { headers: await authHeaders() });
      if (!r.ok) return;
      setPreview(await r.json());
    } catch {
      // preview is informatief, stil falen
    }
  }, []);

  const fetchVoices = useCallback(async () => {
    try {
      setVoicesError(null);
      const r = await fetch('/api/telephony/voices', { headers: await authHeaders() });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: `voices failed: ${r.status}` }));
        throw new Error(err.error || `voices failed: ${r.status}`);
      }
      setVoicesData((await r.json()) as VoicesData);
    } catch (err) {
      setVoicesError(err instanceof Error ? err.message : String(err));
    } finally {
      setVoicesLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchSettings();
    void fetchVoices();
  }, [fetchSettings, fetchVoices]);

  useEffect(() => {
    void fetchPreview(previewAt || undefined);
    const timer = window.setInterval(() => void fetchPreview(previewAt || undefined), 60_000);
    return () => window.clearInterval(timer);
    // settings?.phone_number als dep: bij merk-wissel direct de preview van dát nummer.
  }, [fetchPreview, previewAt, settings?.phone_number]);

  // Volgende open/dicht-wissel vandaag + wat er dán gebeurt (via preview-API,
  // zodat de begroetings-resolutie server-side blijft).
  useEffect(() => {
    if (!settings || previewAt) {
      setNextTransition(null);
      return;
    }
    const t = nextTransitionToday(settings.schedule, settings.overrides, settings.timezone);
    if (!t) {
      setNextTransition(null);
      return;
    }
    let cancelled = false;
    setNextTransition({ label: t.label, opens: t.opens, decision: null });
    void (async () => {
      try {
        const r = await fetch(
          `/api/telephony/routing-preview?at=${encodeURIComponent(t.atIso)}&number=${encodeURIComponent(settings.phone_number)}`,
          { headers: await authHeaders() },
        );
        if (!r.ok || cancelled) return;
        const json = (await r.json()) as PreviewResponse;
        if (!cancelled) setNextTransition({ label: t.label, opens: t.opens, decision: json.decision });
      } catch {
        // informatief, stil falen
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [settings, previewAt]);

  const mutate = useCallback((patch: Partial<SettingsRow>) => {
    setSettings((s) => (s ? { ...s, ...patch } : s));
    setSaveState('dirty');
  }, []);

  const save = useCallback(async (opts?: { applyScheduleToAllNumbers?: boolean }) => {
    if (!settings) return;
    setSaveState('saving');
    setError(null);
    try {
      const r = await fetch('/api/telephony/settings', {
        method: 'PUT',
        headers: await authHeaders(),
        body: JSON.stringify({
          phoneNumber: settings.phone_number,
          schedule: settings.schedule,
          in_hours_action: settings.in_hours_action,
          overflow_action: settings.overflow_action,
          closed_action: settings.closed_action,
          ring_timeout_seconds: settings.ring_timeout_seconds,
          record_calls: settings.record_calls,
          announce_greeting_id: settings.announce_greeting_id,
          overrides: settings.overrides.map((o) => ({
            date: o.date,
            label: o.label,
            closed: o.closed,
            blocks: o.blocks,
            action_override: o.action_override ?? null,
          })),
          ...(opts?.applyScheduleToAllNumbers ? { applyScheduleToAllNumbers: true } : {}),
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: 'Opslaan mislukt' }));
        throw new Error(err.error || 'Opslaan mislukt');
      }
      const json = await r.json();
      const fresh = json.settings as SettingsRow;
      setSettings(fresh);
      setRows((rs) => rs.map((row) => (row.id === fresh.id ? fresh : row)));
      setSaveState('saved');
      // Rooster naar alle merken raakt ook de andere rijen → alles verversen.
      if (opts?.applyScheduleToAllNumbers) void fetchSettings();
      void fetchPreview(previewAt || undefined);
      window.setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaveState('dirty');
    }
  }, [settings, fetchPreview, fetchSettings, previewAt]);

  // "Toepassen op alle merken": bevestiging met de andere nummers, dan één
  // PUT die dit rooster (alléén het rooster) naar alle rijen schrijft.
  const applyScheduleToAll = useCallback(() => {
    if (!settings) return;
    const others = rows.filter((r) => r.id !== settings.id);
    if (others.length === 0) return;
    const list = others
      .map((o) => `- ${BRAND_LABELS[o.brand] ?? o.brand} (${o.phone_number})`)
      .join('\n');
    const ok = window.confirm(
      `Openingstijden van ${BRAND_LABELS[settings.brand] ?? settings.brand} toepassen op:\n${list}\n\nAlleen het weekrooster gaat mee; acties en uitzonderingsdagen blijven per merk.`,
    );
    if (!ok) return;
    void save({ applyScheduleToAllNumbers: true });
  }, [settings, rows, save]);

  const submitAddNumber = useCallback(async () => {
    const phoneNumber = addNumber.replace(/[\s-]/g, '');
    if (!addBrand || !phoneNumber) return;
    if (!/^\+[1-9]\d{6,14}$/.test(phoneNumber)) {
      setError('Vul een geldig E.164-nummer in (bijv. +31850835117)');
      return;
    }
    setAddBusy(true);
    setError(null);
    try {
      const r = await fetch('/api/telephony/settings', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ phoneNumber, brand: addBrand }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: 'Nummer toevoegen mislukt' }));
        throw new Error(err.error || 'Nummer toevoegen mislukt');
      }
      setShowAddDialog(false);
      setAddBrand('');
      setAddNumber('');
      await fetchSettings(phoneNumber);
      setSaveState('idle');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setAddBusy(false);
    }
  }, [addBrand, addNumber, fetchSettings]);

  const announceOptions = useMemo(
    () => (settings?.availableGreetings ?? []).filter((g) => g.kind === 'announce'),
    [settings?.availableGreetings],
  );

  // "Schrijf begroeting" op een uitzonderingsdag: generator prefillen met de
  // feestdag-preset + label/datum, en onthouden welke rij wacht op koppeling.
  const handleWriteGreeting = useCallback(
    (index: number) => {
      const o = settings?.overrides[index];
      if (!o) return;
      pendingOverrideIndexRef.current = index;
      setGeneratorPrefill({
        preset: 'feestdag',
        context: `${o.label?.trim() || 'Uitzonderingsdag'} (${o.date})`,
        kind: 'closed',
        requestId: Date.now(),
      });
      document.getElementById('begroetingen')?.scrollIntoView({ behavior: 'smooth' });
    },
    [settings?.overrides],
  );

  // Nieuw aangemaakte begroeting automatisch aan de wachtende override hangen
  // (wordt pas persistent bij Opslaan, zoals elke andere wijziging op de pagina).
  // Bewust géén fetchSettings hier: dat zou onopgeslagen wijzigingen wissen;
  // de nieuwe begroeting wordt lokaal in availableGreetings gemerged.
  const handleGreetingCreated = useCallback((g: { id: string; name: string; kind: string }) => {
    const index = pendingOverrideIndexRef.current;
    pendingOverrideIndexRef.current = null;
    setSettings((s) => {
      if (!s) return s;
      const availableGreetings = s.availableGreetings.some((x) => x.id === g.id)
        ? s.availableGreetings
        : [{ id: g.id, name: g.name, kind: g.kind, active: false }, ...s.availableGreetings];
      const overrides =
        index !== null && s.overrides[index]
          ? s.overrides.map((o, i) =>
              i === index ? { ...o, action_override: { type: 'voicemail' as const, greeting_id: g.id } } : o,
            )
          : s.overrides;
      return { ...s, availableGreetings, overrides };
    });
    if (index !== null) setSaveState('dirty');
  }, []);

  return (
    <>
      <div className="h-full overflow-y-auto p-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-heading font-semibold text-main">Telefoon-instellingen</h1>
            {settings && (
              <p className="text-sm text-slate-500 mt-1">
                {BRAND_LABELS[settings.brand] ?? settings.brand} · {settings.phone_number}
                {settings.updated_by && ` · laatst gewijzigd door ${settings.updated_by}`}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => void save()}
            disabled={saveState !== 'dirty'}
            className={
              saveState === 'dirty'
                ? 'px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg'
                : 'px-4 py-2 bg-slate-100 text-slate-400 text-sm font-medium rounded-lg cursor-not-allowed'
            }
          >
            {saveState === 'saving' ? 'Opslaan…' : saveState === 'saved' ? 'Opgeslagen' : 'Opslaan'}
          </button>
        </div>

        {/* Merk-switcher: één chip per nummer + nummer toevoegen */}
        {rows.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 mb-6">
            {rows.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => switchToNumber(r.phone_number)}
                className={
                  r.phone_number === settings?.phone_number
                    ? 'px-3 py-1.5 rounded-full bg-indigo-600 text-white text-xs font-medium'
                    : 'px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 hover:border-indigo-300 text-xs font-medium'
                }
              >
                {BRAND_LABELS[r.brand] ?? r.brand}
                <span className={r.phone_number === settings?.phone_number ? 'ml-1.5 text-indigo-200' : 'ml-1.5 text-slate-400'}>
                  {r.phone_number}
                </span>
              </button>
            ))}
            {ALL_BRANDS.some((b) => !rows.some((r) => r.brand === b)) && (
              <button
                type="button"
                onClick={() => setShowAddDialog(true)}
                className="px-3 py-1.5 rounded-full border border-dashed border-slate-300 text-slate-500 hover:border-indigo-400 hover:text-indigo-600 text-xs font-medium"
              >
                + Nummer toevoegen
              </button>
            )}
          </div>
        )}

        {/* Nummer toevoegen (copy-on-create van de Kleda-rij) */}
        {showAddDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40">
            <div className="bg-white rounded-xl shadow-xl border border-slate-200 p-5 w-full max-w-md">
              <h2 className="text-lg font-semibold text-slate-800 mb-1">Nummer toevoegen</h2>
              <p className="text-xs text-slate-500 mb-4">
                Rooster, acties en stem worden gekopieerd van Kleda; begroetingen stel je hierna per merk in.
              </p>
              <div className="space-y-3">
                <div>
                  <label htmlFor="add-brand" className="block text-xs text-slate-500 mb-1">Merk</label>
                  <select
                    id="add-brand"
                    value={addBrand}
                    onChange={(e) => setAddBrand(e.target.value)}
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"
                  >
                    <option value="">Kies een merk…</option>
                    {ALL_BRANDS.filter((b) => !rows.some((r) => r.brand === b)).map((b) => (
                      <option key={b} value={b}>{BRAND_LABELS[b]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="add-number" className="block text-xs text-slate-500 mb-1">Telefoonnummer (E.164)</label>
                  <input
                    id="add-number"
                    type="tel"
                    placeholder="+31850835117"
                    value={addNumber}
                    onChange={(e) => setAddNumber(e.target.value)}
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm font-mono"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <button
                  type="button"
                  onClick={() => setShowAddDialog(false)}
                  className="px-3 py-1.5 rounded text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200"
                >
                  Annuleren
                </button>
                <button
                  type="button"
                  onClick={() => void submitAddNumber()}
                  disabled={addBusy || !addBrand || !addNumber.trim()}
                  className={
                    addBusy || !addBrand || !addNumber.trim()
                      ? 'px-3 py-1.5 rounded text-sm font-medium bg-slate-100 text-slate-400 cursor-not-allowed'
                      : 'px-3 py-1.5 rounded text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700'
                  }
                >
                  {addBusy ? 'Toevoegen…' : 'Toevoegen'}
                </button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 text-red-800 rounded text-sm">{error}</div>
        )}

        {isLoading || !settings ? (
          <div className="space-y-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-40 bg-white border border-slate-200 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Beller-reis: wat hoort de klant, nu of op een gekozen moment */}
            <Card>
              <CardHeader>
                <CardTitle>{previewAt ? 'Beller-reis (ander moment)' : 'Nu'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <label htmlFor="preview-at" className="text-xs text-slate-500">
                    Bekijk een ander moment
                  </label>
                  <input
                    id="preview-at"
                    type="datetime-local"
                    value={previewAt}
                    onChange={(e) => setPreviewAt(e.target.value)}
                    className="border border-slate-200 rounded px-2 py-1 text-xs"
                  />
                  {previewAt && (
                    <button
                      type="button"
                      onClick={() => setPreviewAt('')}
                      className="px-2 py-1 rounded bg-slate-100 text-slate-600 hover:bg-slate-200 text-xs font-medium"
                    >
                      Terug naar nu
                    </button>
                  )}
                </div>

                {preview ? (
                  <div className="space-y-1.5 text-sm">
                    {!preview.flagActive && (
                      <div className="px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 rounded text-xs mb-2">
                        Routering v2 staat nog uit (TELEPHONY_ROUTING_V2). Deze instellingen worden pas
                        gebruikt na de cutover; tot die tijd geldt het oude gedrag.
                      </div>
                    )}
                    {[preview.decision, preview.afterRingTimeout, preview.afterForwardFail].some(
                      (d) => d && d.stage === 'voicemail' && !d.greetingUrl,
                    ) && (
                      <div className="px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 rounded text-xs mb-2">
                        Geen actieve begroeting: de beller hoort op dit moment een neutrale computerstem.{' '}
                        <a href="#begroetingen" className="underline font-medium">
                          Maak of activeer een begroeting
                        </a>
                        .
                      </div>
                    )}
                    {previewAt && (
                      <div className="text-xs text-slate-500 mb-1">
                        Bekeken moment:{' '}
                        {new Date(preview.at).toLocaleString('nl-NL', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    )}
                    <div className="text-slate-800">
                      <span className={preview.decision.isOpen ? 'text-emerald-700 font-medium' : 'text-slate-500 font-medium'}>
                        {preview.decision.isOpen ? 'Open' : 'Gesloten'}
                      </span>
                      {' — '}
                      {describeDecision(preview.decision, preview.ringGroup)}
                      <GreetingLine d={preview.decision} onPlay={playAudio} />
                    </div>
                    {preview.decision.announceUrl && (
                      <div className="text-slate-500 text-xs">
                        Eerst aankondiging "{preview.decision.announceName ?? 'onbekend'}"{' '}
                        <button
                          type="button"
                          onClick={() => playAudio(preview.decision.announceUrl as string)}
                          className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-slate-200 text-[11px] font-medium"
                        >
                          Beluister
                        </button>
                      </div>
                    )}
                    {preview.afterRingTimeout && (
                      <div className="text-slate-500 text-xs">
                        Geen antwoord na {settings.ring_timeout_seconds}s → {describeDecision(preview.afterRingTimeout, preview.ringGroup)}
                        <GreetingLine d={preview.afterRingTimeout} onPlay={playAudio} />
                      </div>
                    )}
                    {preview.afterForwardFail && (
                      <div className="text-slate-500 text-xs">
                        Doorschakeling faalt → {describeDecision(preview.afterForwardFail, preview.ringGroup)}
                        <GreetingLine d={preview.afterForwardFail} onPlay={playAudio} />
                      </div>
                    )}
                    {nextTransition && !previewAt && (
                      <div className="text-xs text-slate-500 pt-2 mt-2 border-t border-slate-100">
                        Om {nextTransition.label} {nextTransition.opens ? 'openen' : 'sluiten'} we
                        {nextTransition.decision && (
                          <>
                            , daarna: {describeDecision(nextTransition.decision, preview.ringGroup)}
                            <GreetingLine d={nextTransition.decision} onPlay={playAudio} />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">Preview laden…</p>
                )}
              </CardContent>
            </Card>

            {/* Openingstijden */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <CardTitle>Openingstijden</CardTitle>
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={applyScheduleToAll}
                      disabled={saveState === 'saving'}
                      className="px-2.5 py-1 rounded text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200"
                      title="Schrijft alleen het weekrooster naar alle merk-nummers"
                    >
                      Toepassen op alle merken
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <OpeningstijdenEditor
                  schedule={settings.schedule}
                  overrides={settings.overrides}
                  onScheduleChange={(schedule) => mutate({ schedule })}
                  onOverridesChange={(overrides) => mutate({ overrides })}
                  greetings={settings.availableGreetings}
                  onWriteGreeting={handleWriteGreeting}
                />
              </CardContent>
            </Card>

            {/* Acties */}
            <Card>
              <CardHeader>
                <CardTitle>Wat gebeurt er</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="divide-y divide-slate-100">
                  <ActionEditor
                    label="Binnen openingstijden"
                    hint="Eerste actie bij een inkomend gesprek"
                    action={settings.in_hours_action}
                    allowRing
                    greetings={settings.availableGreetings.filter((g) => g.kind !== 'announce')}
                    onChange={(a) => mutate({ in_hours_action: a })}
                  />
                  <ActionEditor
                    label="Bij geen antwoord"
                    hint={`Na ${settings.ring_timeout_seconds} seconden rinkelen`}
                    action={settings.overflow_action}
                    allowRing={false}
                    greetings={settings.availableGreetings.filter((g) => g.kind !== 'announce')}
                    onChange={(a) => mutate({ overflow_action: a })}
                  />
                  <ActionEditor
                    label="Buiten openingstijden"
                    hint="Avond, weekend en uitzonderingsdagen"
                    action={settings.closed_action}
                    allowRing={false}
                    greetings={settings.availableGreetings.filter((g) => g.kind !== 'announce')}
                    onChange={(a) => mutate({ closed_action: a })}
                  />
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
                  <div className="flex items-center gap-3">
                    <label htmlFor="ring-timeout" className="text-sm text-slate-700 w-48">
                      Ring-timeout
                    </label>
                    <input
                      id="ring-timeout"
                      type="range"
                      min={10}
                      max={60}
                      step={5}
                      value={settings.ring_timeout_seconds}
                      onChange={(e) => mutate({ ring_timeout_seconds: Number(e.target.value) })}
                      className="flex-1 max-w-[16rem]"
                    />
                    <span className="text-sm text-slate-600 font-mono w-10">{settings.ring_timeout_seconds}s</span>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={settings.record_calls}
                      onChange={(e) => mutate({ record_calls: e.target.checked })}
                    />
                    Gesprekken opnemen (dual-channel, voor transcriptie en samenvatting)
                  </label>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-700 w-48">Aankondiging vóór rinkelen</span>
                    <select
                      value={settings.announce_greeting_id ?? ''}
                      onChange={(e) => mutate({ announce_greeting_id: e.target.value || null })}
                      className="border border-slate-200 rounded px-2 py-1.5 text-sm"
                    >
                      <option value="">Geen (direct rinkelen)</option>
                      {announceOptions.map((g) => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                    <span className="text-xs text-slate-400">bijv. opname-melding</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI-assistent per nummer (fase 3): registratie bij xAI + het
                actie-slot in één handeling, plus de doofheid-log. */}
            <Card>
              <CardHeader>
                <CardTitle>AI-assistent — {settings.phone_number}</CardTitle>
              </CardHeader>
              <CardContent>
                <AgentPanel
                  key={settings.phone_number}
                  phoneNumber={settings.phone_number}
                  onChanged={() => void fetchSettings(settings.phone_number)}
                />
              </CardContent>
            </Card>

            {/* Stem */}
            <Card>
              <CardHeader>
                <CardTitle>Stem</CardTitle>
              </CardHeader>
              <CardContent>
                <VoicePicker data={voicesData} loading={voicesLoading} error={voicesError} onRefresh={() => void fetchVoices()} />
              </CardContent>
            </Card>

            {/* Begroetingen (merk-gescoped: key forceert verse lijst bij merk-wissel) */}
            <div id="begroetingen">
              <Card>
                <CardHeader>
                  <CardTitle>Begroetingen — {BRAND_LABELS[settings.brand] ?? settings.brand}</CardTitle>
                </CardHeader>
                <CardContent>
                  <GreetingsManager
                    key={settings.brand}
                    brand={settings.brand}
                    voices={voicesData}
                    prefill={generatorPrefill}
                    onCreated={handleGreetingCreated}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Ring group */}
            <Card>
              <CardHeader>
                <CardTitle>Ring group</CardTitle>
              </CardHeader>
              <CardContent>
                <RingGroupManager />
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
